import { useMutation, useQuery } from '@apollo/client';
import { produce } from 'immer';
import { useCallback } from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { useReviewHasuraContext } from '@eluve/session-helpers';

import { GoldenSummaryFactFormData } from './GoldenSummaryFactForm';
import {
  createGoldenFactMutation,
  deleteGoldenFactMutation,
  getGoldenFactsQuery,
  orderedGoldenFactListFragment,
  updateGoldenFactMutation,
} from './operations';

export const useGoldenSummaryFacts = (
  tenantId: string,
  appointmentId: string,
) => {
  const toast = useToast();
  const context = useReviewHasuraContext();
  const { data, loading, refetch } = useQuery(getGoldenFactsQuery, {
    context,
    variables: {
      tenantId,
      appointmentId,
    },
  });

  const [createFact, { loading: isCreating }] = useMutation(
    createGoldenFactMutation,
    {
      onCompleted: (_data) => {
        toast.success('Fact created successfully');
      },
      optimisticResponse: (input) => ({
        insertAppointmentGoldenFactsOne: {
          __typename: 'AppointmentGoldenFacts' as const,
          id: 'temp-id',
          tenantId,
          appointmentId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          statement: input.statement ?? '',
          expectedOutput: input.expectedOutput ?? true,
          weightMultiplier: input.weightMultiplier ?? 0.6,
          isVerified: false,
        },
      }),
      update: (_cache, { data }) => {
        if (!data?.insertAppointmentGoldenFactsOne) return;
        cacheUtils.updateFragment(
          {
            fragment: orderedGoldenFactListFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) =>
            produce(existing, (draft) => {
              if (!draft || !data.insertAppointmentGoldenFactsOne) return;
              draft.appointment_golden_facts.unshift(
                data.insertAppointmentGoldenFactsOne,
              );
            }),
        );
      },
      onError: (error) => {
        toast.error(`Failed to add fact: ${error.message}`);
      },
    },
  );

  const [updateFact, { loading: isUpdating }] = useMutation(
    updateGoldenFactMutation,
    {
      onCompleted: () => {
        toast.success('Fact updated successfully');
      },
      optimisticResponse: (input) => ({
        updateAppointmentGoldenFactsByPk: {
          __typename: 'AppointmentGoldenFacts' as const,
          id: input.id,
          tenantId,
          appointmentId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          statement: input.statement ?? '',
          expectedOutput: input.expectedOutput ?? true,
          weightMultiplier: input.weightMultiplier ?? 0.6,
          isVerified: input.isVerified ?? false,
        },
      }),
      onError: (error) => {
        toast.error(`Failed to update fact: ${error.message}`);
      },
    },
  );

  const [deleteFact, { loading: isDeleting }] = useMutation(
    deleteGoldenFactMutation,
    {
      onCompleted: () => {
        toast.success('Fact deleted successfully');
      },
      optimisticResponse: (variables) => ({
        deleteAppointmentGoldenFactsByPk: {
          __typename: 'AppointmentGoldenFacts',
          id: variables.id,
        },
      }),
      update: (_cache, { data }) => {
        if (!data?.deleteAppointmentGoldenFactsByPk) return;
        cacheUtils.updateFragment(
          {
            fragment: orderedGoldenFactListFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) =>
            produce(existing, (draft) => {
              if (!draft || !data.deleteAppointmentGoldenFactsByPk) return;
              const deletedFactId = data.deleteAppointmentGoldenFactsByPk.id;
              draft.appointment_golden_facts =
                draft.appointment_golden_facts.filter(
                  (fact) => fact.id !== deletedFactId,
                );
            }),
        );
      },
      onError: (error) => {
        toast.error(`Failed to delete fact: ${error.message}`);
      },
    },
  );

  const handleAddNewFact = useCallback(() => {
    createFact({
      variables: {
        tenantId,
        appointmentId,
        statement: '',
        expectedOutput: true,
        weightMultiplier: 0.6,
      },
    });
  }, [createFact, tenantId, appointmentId]);

  const handleFactUpdate = useCallback(
    (factData: GoldenSummaryFactFormData) => {
      updateFact({
        variables: {
          id: factData.id,
          tenantId,
          statement: factData.statement,
          expectedOutput: factData.expectedOutput,
          weightMultiplier: factData.weightMultiplier,
          isVerified: factData.isVerified,
        },
      });
    },
    [updateFact, tenantId],
  );

  const handleFactDelete = useCallback(
    (id: string) => {
      deleteFact({
        variables: {
          id,
          tenantId,
        },
      });
    },
    [deleteFact, tenantId],
  );

  return {
    facts: data?.appointmentsByPk?.appointment_golden_facts || [],
    loading,
    isCreating,
    isUpdating,
    isDeleting,
    handleAddNewFact,
    handleFactUpdate,
    handleFactDelete,
    refetch,
  };
};
